import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import { 
  Package, 
  TrendingUp, 
  TrendingDown,
  AlertCircle, 
  DollarSign, 
  Activity,
  BarChart3,
  Calendar,
  Users,
  // Truck removed - using image asset instead
  Building2,
  Target,
  Clock,
  ArrowUp,
  ArrowDown,
  Percent,
  Calculator,
  RefreshCw,
  FileText,
  ShoppingCart,
  Briefcase,
  FileCheck,
  Home,
  Settings,
  Clipboard,
  PieChart as PieChartIcon,
  Boxes,
  LogIn,
  LogOut,
  Database,
  UserCheck,
  Archive,
  BarChart2,
  Layers,
  Repeat,
  Check,
  CheckCircle,
  Award,
  Phone,
  Mail,
  User,
  MapPin,
  ChevronDown,
  Wrench,
  Map,
  Gauge,
  Truck,
  Download,
  X,
  Printer
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LabelList,
  PieChart as ReChartsPie,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  Radar,
  Treemap,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Scatter,
  ScatterChart,
  ZAxis
} from 'recharts';
import { productsService } from '../services/productsService';
import { entriesService } from '../services/entriesService';
import { exitsService } from '../services/exitsService';
import { suppliersService } from '../services/suppliersService';
import { loansService } from '../services/loansService';
import { testBackendConnection } from '../services/api';
import { trucksService } from '../services/trucksService';

const SummaryPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('inventory');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para el modal del reporte general
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportMode, setReportMode] = useState('single'); // 'single' o 'range'
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // Datos originales para diagnóstico
  const [products, setProducts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [exits, setExits] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  
  const [data, setData] = useState({
    // KPIs Principales
    totalProducts: 0,
    totalValue: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
    totalSuppliers: 0,
    
    // Movimientos
    totalEntries: 0,
    totalExits: 0,
    entriesValue: 0,
    exitsValue: 0,
    netMovement: 0,
    
    // Tendencias (último mes)
    entriesThisMonth: 0,
    exitsThisMonth: 0,
    entriesLastMonth: 0,
    exitsLastMonth: 0,
    
    // Top productos
    topValueProducts: [],
    lowStockProducts: [],
    outOfStockProducts: [],
    topMovementProducts: [],
    
    // Datos para gráficas
    monthlyMovements: [],
    categoryDistribution: [{ name: 'Sin datos', count: 1, value: 0 }], // Valor por defecto para evitar gráficas vacías
    supplierPerformance: [],
    stockLevels: [
      { name: 'Sin Stock', count: 0, color: '#ef4444' },
      { name: 'Stock Bajo', count: 0, color: '#f59e0b' },
      { name: 'Stock Normal', count: 1, color: '#10b981' }
    ],
    
    // Métricas de rendimiento
    averageEntryValue: 0,
    averageExitValue: 0,
    turnoverRate: 0,
    stockEfficiency: 0,
    
    // Datos de proveedores
    suppliers: [],
    topSuppliers: [],
    suppliersWithInactivity: [],
    suppliersByCategory: [],
    
    // Datos de préstamos
    loans: [],
    activeLoans: [],
    loansByStatus: [],
    overdueLoanCount: 0,
    
    // Datos de historial
    recentActivity: [],
    activityByType: [],
    userActivity: [],
    
    // Datos de flotilla (placeholder para futura implementación)
    fleetStats: []
  });

  // Función para formatear dinero
  const formatMoney = (amount) => {
    if (!amount) return '$0';
    
    const number = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(number)) return '$0';
    
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(number);
  };

  // Función para formatear números grandes
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };
  
  // Función para navegar al detalle del préstamo
  const goToLoanDetail = (loanId) => {
    if (loanId) {
      console.log('Navegando a detalle del préstamo:', loanId);
      navigate(`/loans/${loanId}`);
    }
  };

  // Función para calcular porcentaje de cambio
  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Debug logging: imprimir en consola los datos que alimentan las gráficas
  useEffect(() => {
    try {
      console.log('🧭 DEBUG categoryDistribution:', data.categoryDistribution);
      console.log('🧭 DEBUG stockLevels:', data.stockLevels);
      console.log('🧭 DEBUG supplierDistribution (from state):', data.supplierDistribution);
      console.log('🧭 DEBUG monthlyPurchases (from state):', data.monthlyPurchases);
    } catch (err) {
      console.error('Error al loggear datos debug:', err);
    }
  }, [data.categoryDistribution, data.stockLevels, data.supplierDistribution, data.monthlyPurchases]);

  // Estado para rastrear errores API específicos
  const [apiError, setApiError] = useState({
    products: false,
    entries: false,
    exits: false,
    suppliers: false
  });

  // Debug flag to inspect computed graph data in the UI
  const [showDebugData, setShowDebugData] = useState(false);
  // Mostrar lista completa de categorías cuando las tiles son pequeñas
  const [showCategoryList, setShowCategoryList] = useState(false);
  // Configuración de la gráfica de velocidad
  const [velocityWindowDays, setVelocityWindowDays] = useState(30);
  const [showAllVelocity, setShowAllVelocity] = useState(false);
  const [useAllHistory, setUseAllHistory] = useState(false);

  // Date filters: presets + custom range
  const [selectedPreset, setSelectedPreset] = useState('30d');
  const [startDate, setStartDate] = useState(''); // yyyy-mm-dd
  const [endDate, setEndDate] = useState('');   // yyyy-mm-dd

  const loadDashboardData = async () => {
      // Definir totalFleetUnits para evitar el error de referencia
      let totalFleetUnits = 0;
      

    try {
      setLoading(true);
      setError(null);
      setApiError({
        products: false,
        entries: false,
        exits: false,
        suppliers: false
      });
      console.log('🔄 Cargando datos del dashboard...');

      // Primero verificar conectividad con el backend
      console.log('🔍 Probando conectividad con el backend...');
      const connectionTest = await testBackendConnection();
      if (!connectionTest.success) {
        console.error('❌ Error de conectividad con el backend:', connectionTest.error);
        throw new Error('No se pudo conectar con el servidor. Verifique que el backend esté funcionando.');
      }
      
      console.log('✅ Backend conectado correctamente en:', connectionTest.port);
      
      // Cargar todos los datos en paralelo
      console.log('📡 Llamando a las APIs...');
      let productsRes, entriesRes, exitsRes, suppliersRes;

      try {
        productsRes = await productsService.getAllUnpaginated();
        console.log('✅ Productos cargados:', productsRes);
        // getAllUnpaginated devuelve directamente el array, no { data: [] }
        if (!Array.isArray(productsRes)) {
          productsRes = [];
        }
      } catch (err) {
        console.error('⚠️ Error cargando productos:', err);
        setApiError(prev => ({...prev, products: true}));
        productsRes = [];
      }

      try {
        entriesRes = await entriesService.getAll(1, 100000);
        console.log('✅ Entradas cargadas:', entriesRes);
      } catch (err) {
        console.error('⚠️ Error cargando entradas:', err);
        setApiError(prev => ({...prev, entries: true}));
        entriesRes = { data: [] };
      }

      try {
        exitsRes = await exitsService.getAll(1, 100000);
        console.log('✅ Salidas cargadas:', exitsRes);
      } catch (err) {
        console.error('⚠️ Error cargando salidas:', err);
        setApiError(prev => ({...prev, exits: true}));
        exitsRes = { data: [] };
      }

      try {
        suppliersRes = await suppliersService.getAll();
        console.log('✅ Proveedores cargados:', suppliersRes);
      } catch (err) {
        console.error('⚠️ Error cargando proveedores:', err);
        setApiError(prev => ({...prev, suppliers: true}));
        suppliersRes = { data: [] };
      }

      // Cargar conteo de productos por proveedor
      let supplierProductCounts = {};
      try {
        supplierProductCounts = await suppliersService.getProductCounts();
        console.log('✅ Conteo de productos por proveedor cargado:', supplierProductCounts);
      } catch (err) {
        console.error('⚠️ Error cargando conteo de productos:', err);
        supplierProductCounts = {};
      }

      // Cargar préstamos (loans) y estadísticas relacionadas
      let loansRes, loansStatsRes;
      try {
        loansRes = await loansService.getAll();
        console.log('✅ Préstamos cargados:', loansRes);
      } catch (err) {
        console.error('⚠️ Error cargando préstamos:', err);
        loansRes = { data: [] };
      }

      try {
        loansStatsRes = await loansService.getStats().catch(() => null);
        console.log('ℹ️ Préstamos - stats obtenidas:', loansStatsRes);
      } catch (err) {
        console.warn('⚠️ No se pudieron obtener estadísticas de préstamos desde el servidor, se calcularán localmente');
        loansStatsRes = null;
      }
      
      // Cargar datos de flotilla desde la API (MySQL) usando trucksService
      let fleetRes, fleetStatsRes;
      try {
        fleetRes = await trucksService.getAll();
        console.log('✅ Vehículos cargados desde API:', fleetRes);
      } catch (err) {
        console.error('⚠️ Error cargando vehículos desde API:', err);
        fleetRes = { data: [] };
      }
      
      try {
        // trucksService.getStats may not exist in service; prefer calling backend endpoint if present
        if (trucksService.getStats) {
          fleetStatsRes = await trucksService.getStats();
          console.log('ℹ️ Flotilla - stats obtenidas desde service:', fleetStatsRes);
        } else {
          // Fallback: try API endpoint via api module or leave null
          fleetStatsRes = null;
        }
      } catch (err) {
        console.warn('⚠️ No se pudieron obtener estadísticas de flotilla desde service:', err);
        fleetStatsRes = null;
      }

  // getAllUnpaginated devuelve directamente el array, no un objeto con .data
  const products = Array.isArray(productsRes) ? productsRes : [];
  const rawEntries = entriesRes.data || [];
  const rawExits = exitsRes.data || [];
  const fleet = fleetRes.data || [];

      // --- Conteo por tipo de unidad (desde MySQL vía trucksService.getAll) ---
      // Normalizar usando trucksService.normalizeTipoUnidad para agrupar variantes
      const countsByTipo = {};
      try {
        // Diagnostic: show sample and total fleet size
        try { console.log('🚚 DEBUG fleet sample (first 5):', (fleet || []).slice(0,5)); } catch(e){}

        // Solo contar vehículos que estén en estados relevantes: activos o en mantenimiento
        const includeStatusRegex = /activo|mantenimiento|preventivo|correctivo|reparaci/i;
        const excludeStatusRegex = /baja|dado de baja|inactivo|retirad|retired|removed/i;

        let includedCount = 0;
        (fleet || []).forEach(v => {
          if (!v) return;
          // Determinar estado normalizado desde distintos posibles campos (incluye 'estatus' usado por trucksService)
          const rawStatus = (v.status || v.estatus || v.state || v.estado || '').toString();
          const statusNorm = rawStatus.trim();

          // Excluir explícitamente estados de baja/inactivo
          if (excludeStatusRegex.test(statusNorm)) {
            // console.log('🔍 Excluyendo por estado (baja/inactivo):', statusNorm, v.plates || v.id || v);
            return;
          }

          // Incluir sólo si el estado indica activo o mantenimiento/reparación/preventivo/correctivo
          if (!includeStatusRegex.test(statusNorm)) {
            // También aceptar si existe un flag booleano 'active' en el registro
            if (!(v.active === true || v.is_active === true || v.enabled === true)) {
              // console.log('🔍 Excluyendo por estado desconocido y sin flag activo:', statusNorm, v.plates || v.id || v);
              return;
            }
          }

          // Priorizar campo exacto 'tipo_unidad', pero aceptar aliases
          const rawTipo = v.tipo_unidad || v.tipoUnidad || v.tipo || v.category || v.tipo || '';
          const key = trucksService.normalizeTipoUnidad(rawTipo) || 'otros';
          countsByTipo[key] = (countsByTipo[key] || 0) + 1;
          includedCount += 1;
        });

        // If nothing was included, fallback: count by type ignoring status (helps recover graph)
        if ((Object.keys(countsByTipo).length === 0 || includedCount === 0) && (fleet || []).length > 0) {
          console.warn('⚠️ Warning: filtro por estado excluyó todos los vehículos. Recalculando conteos SIN filtrar por estado.');
          // reset and recount without status checks
          Object.keys(countsByTipo).forEach(k => delete countsByTipo[k]);
          (fleet || []).forEach(v => {
            if (!v) return;
            const rawTipo = v.tipo_unidad || v.tipoUnidad || v.tipo || v.category || v.tipo || '';
            const key = trucksService.normalizeTipoUnidad(rawTipo) || 'otros';
            countsByTipo[key] = (countsByTipo[key] || 0) + 1;
          });
        }

        try { console.log('🚚 DEBUG countsByTipo:', countsByTipo, 'includedCount:', includedCount, 'fleetTotal:', (fleet || []).length); } catch(e){}
      } catch (err) {
        console.warn('Error calculando countsByTipo desde fleet:', err);
      }

      // Mapear keys normalizadas a campos usados por la UI
      const categoriesFromFleet = {
        tracto: countsByTipo['tractocamion'] || countsByTipo['tracto'] || countsByTipo['tractocamión'] || 0,
        dolly: countsByTipo['dolly'] || 0,
        autotanque: countsByTipo['autotanque'] || 0,
        cajaseca: countsByTipo['cajaseca'] || 0,
        semiremolque: countsByTipo['semiremolque'] || 0,
        grua: countsByTipo['grua'] || 0,
        unidadgas: countsByTipo['unidadgas'] || 0,
        camioneta: countsByTipo['camioneta'] || 0,
        otros: Object.keys(countsByTipo).reduce((s,k) => {
          const known = ['tractocamion','tracto','tractocamión','dolly','autotanque','cajaseca','semiremolque','grua','unidadgas','camioneta'];
          return s + (known.includes(k) ? 0 : (countsByTipo[k] || 0));
        }, 0)
      };

      // Construir array para Treemap / otras gráficas
      const truckTypeDistribution = Object.entries(categoriesFromFleet).map(([k, v]) => ({
        name: (
          k === 'tracto' ? 'Tracto Camión' :
          k === 'dolly' ? 'Dolly' :
          k === 'autotanque' ? 'Auto Tanque' :
          k === 'cajaseca' ? 'Caja Seca' :
          k === 'semiremolque' ? 'Semi Remolque' :
          k === 'grua' ? 'Grúa' :
          k === 'unidadgas' ? 'Unidad Gas' :
          k === 'camioneta' ? 'Camioneta' : 'Otros'
        ),
        value: Number(v) || 0,
        count: Number(v) || 0
      })).filter(item => item.value >= 0);

      // Apply date filters client-side (entries/exits). If no range selected, return original arrays.
      const filterByRange = (items, dateFields) => {
        if (!startDate && !endDate) return items;
        let s = startDate ? new Date(startDate) : null;
        let e = endDate ? new Date(endDate) : null;
        if (e) e.setHours(23,59,59,999);
        return (items || []).filter(item => {
          if (!item) return false;
          // Accept array of possible fields
          const fields = Array.isArray(dateFields) ? dateFields : [dateFields];
          let raw = null;
          for (const f of fields) {
            if (item[f]) { raw = item[f]; break; }
          }
          // fallback common fields
          if (!raw) raw = item.entry_date || item.exit_date || item.date || item.created_at || item.timestamp;
          if (!raw) return false;
          const d = new Date(raw);
          if (isNaN(d)) return false;
          if (s && d < s) return false;
          if (e && d > e) return false;
          return true;
        });
      };

      const entries = filterByRange(rawEntries, ['entry_date', 'created_at']);
      const exits = filterByRange(rawExits, ['exit_date', 'created_at']);
      const suppliers = suppliersRes.data || [];

      // Guardar los datos originales para diagnóstico
      setProducts(products);
      setEntries(entries);
      setExits(exits);
      setSuppliers(suppliers);

      console.log('📊 Datos cargados:', { 
        products: products.length, 
        entries: entries.length, 
        exits: exits.length,
        suppliers: suppliers.length 
      });

      // --- Valores agregados y listas derivadas (defensivas) ---
      // Estos se usan más abajo para métricas y visualizaciones; definimos
      // versiones seguras aquí para evitar ReferenceErrors si alguna estructura
      // falta o cambia de forma.
      const totalProducts = Array.isArray(products) ? products.length : 0;

      // Alinear criterio de "Stock Bajo" con la página de Inventario:
      // Bajo = color amarillo en getStockStatus => current_stock > min_stock y < 70% de max_stock
      const lowStockProducts = (products || [])
        .filter(p => p && typeof p === 'object')
        .filter(p => {
          const current = Number(p.current_stock ?? p.stock ?? p.quantity ?? 0) || 0;
          const min = Number(p.min_stock ?? p.minimum_stock ?? 5) || 0;
          const max = Number(p.max_stock ?? p.maximum_stock ?? 50) || 50;
          if (current === 0) return false;            // agotado (no cuenta aquí)
          if (current <= min) return false;           // crítico (rojo)
          return current < (max * 0.7);               // bajo (amarillo)
        })
        .map(p => ({
          id: p.id || p._id || '',
          name: p.name || p.product_name || 'Sin nombre',
          sku: p.sku || p.code || p.product_code || '',
          current_stock: Number(p.current_stock ?? p.stock ?? p.quantity ?? 0),
          min_stock: Number(p.min_stock ?? p.minimum_stock ?? 5) || 0,
          max_stock: Number(p.max_stock ?? p.maximum_stock ?? 50) || 50
        }));

      const outOfStockProducts = (products || [])
        .filter(p => p && typeof p === 'object')
        .filter(p => Number(p.current_stock ?? p.stock ?? p.quantity ?? 0) === 0)
        .map(p => ({ id: p.id || p._id || '', name: p.name || 'Sin nombre', sku: p.sku || p.code || '' }));

      const totalEntries = Array.isArray(entries) ? entries.length : 0;
      const totalExits = Array.isArray(exits) ? exits.length : 0;

      const entriesValue = (entries || []).reduce((sum, entry) => sum + (parseFloat(entry.total_cost || entry.total || 0) || 0), 0);
      const exitsValue = (exits || []).reduce((sum, exit) => sum + (parseFloat(exit.total_cost || exit.total || 0) || 0), 0);


      // --- Preparar datos de proveedores (fallbacks mínimos) ---
      // El backend puede no devolver métricas agregadas como totalPurchases.
      // Para que la UI muestre algo útil, construimos un listado básico.
      const topSuppliers = (suppliers || []).slice(0, 10).map(s => ({
        id: s.id,
        name: s.name || s.company || `Proveedor ${s.id}`,
        contact: s.contact_person || s.contactPerson || s.contact || '',
        phone: s.phone || s.telefono || '',
        email: s.email || '',
        location: s.address || s.location || '',
        totalPurchases: s.totalPurchases || 0,
        lastOrderDate: s.last_order_date || s.updated_at || '',
        productCount: supplierProductCounts[s.id] || 0  // Usar conteo desde el endpoint
      }));

      // Calcular totales de compras por proveedor a partir de las entradas
      try {
        const supplierTotals = {}; // supplierId -> totalCost

        // Debug sample entry structure
        console.log('🔍 DEBUG entries.length:', entries?.length || 0);
        console.log('🔍 DEBUG sampleEntry[0]:', entries?.[0]);
        console.log('🔍 DEBUG sampleEntry[0].products:', entries?.[0]?.products);
        console.log('🔍 DEBUG sampleEntry[0].product_count:', entries?.[0]?.product_count);
        console.log('🔍 DEBUG topSuppliers ANTES:', topSuppliers);

        (entries || []).forEach(entry => {
          // Normalize supplier id from multiple possible shapes
          let sid = null;
          if (!entry) return;
          sid = entry.supplier_id || entry.supplierId || entry.supplier || null;
          if (!sid && entry.supplier && typeof entry.supplier === 'object') {
            sid = entry.supplier.id || entry.supplier.supplier_id || entry.supplier._id || null;
          }
          if (!sid && entry.meta && typeof entry.meta === 'object') {
            sid = entry.meta.supplier_id || entry.meta.supplierId || null;
          }
          if (!sid) {
            console.log('⚠️ Entry sin supplier_id:', entry);
            return;
          }
          const sidKey = String(sid);
          const entryCost = Number(entry.total_cost || entry.totalCost || 0) || 0;
          console.log(`💰 Proveedor ${sidKey}: +${entryCost} (total: ${(supplierTotals[sidKey] || 0) + entryCost})`);
          supplierTotals[sidKey] = (supplierTotals[sidKey] || 0) + entryCost;
        });
        
        console.log('💵 DEBUG supplierTotals:', supplierTotals);
        
        // Actualizar topSuppliers con los totales calculados
        topSuppliers.forEach(supplier => {
          const sidKey = String(supplier.id);
          const oldTotal = supplier.totalPurchases;
          supplier.totalPurchases = supplierTotals[sidKey] || 0;
          // productCount ya viene desde supplierProductCounts del backend
          console.log(`🔄 Proveedor ${supplier.name} (ID: ${sidKey}): ${oldTotal} → ${supplier.totalPurchases}, productos: ${supplier.productCount}`);
        });

        console.log('✅ DEBUG topSuppliers DESPUÉS:', topSuppliers);
      } catch (err) {
        console.error('❌ Error calculando totales de proveedores:', err);
      }

      // Ensure productMovements exists: build it from entries/exits if not defined
      let productMovements = {};
      try {
        // If the code elsewhere defined it, keep it; otherwise compute a lightweight map
        // from entries/exits arrays (counting movements per product id)
        if (typeof productMovements === 'undefined' || Object.keys(productMovements).length === 0) {
          const pm = {};
          
          // Procesar entradas (ahora pueden tener múltiples productos en entry.products)
          (entries || []).forEach(en => {
            if (!en) return;
            
            // Si tiene un array de productos (nueva estructura con entry_items)
            if (Array.isArray(en.products) && en.products.length > 0) {
              en.products.forEach(prod => {
                const id = prod.product_id || prod.productId || prod.id || null;
                if (!id) return;
                pm[id] = pm[id] || { entries: 0, exits: 0, total: 0 };
                pm[id].entries += Number(prod.quantity || 1);
                pm[id].total = pm[id].entries + pm[id].exits;
              });
            } 
            // Si tiene un product_id directo (estructura antigua)
            else {
              const id = en.product_id || en.productId || null;
              if (!id) return;
              pm[id] = pm[id] || { entries: 0, exits: 0, total: 0 };
              pm[id].entries += Number(en.quantity || 1);
              pm[id].total = pm[id].entries + pm[id].exits;
            }
          });
          
          // Procesar salidas (ahora pueden tener múltiples productos en exit.products)
          console.log('🔍 DEBUG - Total de salidas a procesar:', (exits || []).length);
          if (exits && exits.length > 0) {
            console.log('🔍 DEBUG - Estructura de la primera salida:', exits[0]);
          }
          
          (exits || []).forEach((ex, index) => {
            if (!ex) return;
            
            // Si tiene un array de productos (estructura con exit_items)
            if (Array.isArray(ex.products) && ex.products.length > 0) {
              ex.products.forEach(prod => {
                const id = prod.product_id || prod.productId || prod.id || null;
                if (!id) return;
                pm[id] = pm[id] || { entries: 0, exits: 0, total: 0 };
                pm[id].exits += Number(prod.quantity || 1);
                pm[id].total = pm[id].entries + pm[id].exits;
                
                if (index < 3) {
                  console.log(`🔍 DEBUG - Salida ${index}, Producto:`, {
                    exit_id: ex.id,
                    product_id: id,
                    quantity: Number(prod.quantity || 1)
                  });
                }
              });
            } 
            // Si tiene un product_id directo (estructura antigua sin exit_items)
            else {
              const id = ex.product_id || ex.productId || null;
              const quantity = Number(ex.quantity || 1);
              
              if (index < 3) {
                console.log(`🔍 DEBUG - Salida ${index} (antigua):`, {
                  exit_id: ex.id,
                  product_id: id,
                  quantity: quantity
                });
              }
              
              if (!id) {
                if (index < 3) console.warn('⚠️ Salida sin product_id ni products[]:', ex);
                return;
              }
              
              pm[id] = pm[id] || { entries: 0, exits: 0, total: 0 };
              pm[id].exits += quantity;
              pm[id].total = pm[id].entries + pm[id].exits;
            }
          });
          
          console.log('🔍 DEBUG - ProductMovements después de salidas:', Object.keys(pm).slice(0, 3).map(k => ({ productId: k, data: pm[k] })));
          
          productMovements = pm;
        }
      } catch (err) {
        console.error('Error calculating productMovements:', err);
        productMovements = {};
      }

      const topMovementProducts = products
        .map(product => ({
          ...product,
          movements: productMovements[product.id] || { entries: 0, exits: 0, total: 0 }
        }))
        .sort((a, b) => b.movements.total - a.movements.total)
        .slice(0, 5);

      // --- Cálculo de velocidad (turnover por producto) ---
      // Definimos 'velocidad' como cantidad de movimientos (entradas+salidas)
      // en los últimos 30 días por producto. Esto permite clasificar
      // productos por rapidez de rotación.
      // Ventana dinámica según la opción seleccionada
      const WINDOW_MS = 1000 * 60 * 60 * 24 * (Number(velocityWindowDays) || 30);
      const sinceDate = new Date(Date.now() - WINDOW_MS);

      const recentMovements = [...entries, ...exits].filter(m => {
        const d = new Date(m.entry_date || m.exit_date || m.created_at || null);
        return d instanceof Date && !isNaN(d) && d >= sinceDate;
      });

      const velocityByProduct = {};
      recentMovements.forEach(m => {
        const pid = m.product_id;
        if (!velocityByProduct[pid]) velocityByProduct[pid] = { id: pid, count: 0 };
        velocityByProduct[pid].count++;
      });

      let velocityProductsArray = Object.values(velocityByProduct)
        .map(v => {
          const prod = products.find(p => String(p.id) === String(v.id)) || {};
          return {
            id: String(v.id),
            name: prod.name || prod.product_name || `Producto ${v.id}`,
            sku: prod.sku || prod.code || '',
            movements: v.count || 0
          };
        })
        .sort((a, b) => b.movements - a.movements);

      if (!showAllVelocity) {
        velocityProductsArray = velocityProductsArray.slice(0, 20);
      }

      // --- Cálculo de Talleres con más salidas ---
      // Agrupar salidas por campo `workshop` dentro de la misma ventana de tiempo
      const exitsSince = useAllHistory ? (exits || []) : (exits || []).filter(e => {
        const d = new Date(e.exit_date || e.date || e.created_at || null);
        return d instanceof Date && !isNaN(d) && d >= sinceDate;
      });

      // Agrupar salidas por taller (con nombres alternativos y defensiva)
      const workshopCounts = {};
      try {
        (exitsSince || []).forEach(e => {
          const rawW = e && (e.workshop || e.whorkshop || e.taller || e.workShop);
          const name = rawW ? String(rawW) : 'Sin taller';
          workshopCounts[name] = (workshopCounts[name] || 0) + 1;
        });
      } catch (err) {
        console.error('Error calculando workshopCounts:', err);
      }

      let workshopsArray = Object.entries(workshopCounts).map(([name, count]) => ({
        name,
        exits: count
      }))
      .sort((a, b) => b.exits - a.exits);

      // Auto-fallback: si la ventana reciente solo devuelve 1 (o 0) taller
      // y existen más salidas en total, recalcular usando todo el historial
      if (!useAllHistory && Array.isArray(workshopsArray) && workshopsArray.length <= 1 && (exits || []).length > 1) {
        try {
          const fullCounts = {};
          (exits || []).forEach(e => {
            const rawW = e && (e.workshop || e.whorkshop || e.taller || e.workShop);
            const name = rawW ? String(rawW) : 'Sin taller';
            fullCounts[name] = (fullCounts[name] || 0) + 1;
          });
          const fullArray = Object.entries(fullCounts).map(([name, count]) => ({ name, exits: count })).sort((a,b) => b.exits - a.exits);
          console.warn('⚠️ INFO fallback: la ventana reciente devolvió <=1 taller — usando todo el historial para la gráfica de talleres', { recentCount: workshopsArray.length, totalExits: (exits || []).length, fallbackCount: fullArray.length });
          if (Array.isArray(fullArray) && fullArray.length > 0) {
            workshopsArray = fullArray;
          }
        } catch (err) {
          console.error('Error aplicando fallback de historial completo:', err);
        }
      }

      // DEBUG: mostrar los talleres detectados
      try {
        console.log('🔎 DEBUG useAllHistory:', useAllHistory);
        console.log('🔎 DEBUG workshopCounts keys:', Object.keys(workshopCounts));
        console.log('🔎 DEBUG workshopsArray:', workshopsArray.slice(0, 50));
      } catch (err) {
        console.error('Error debug workshops:', err);
      }

      if (!showAllVelocity) {
        workshopsArray = workshopsArray.slice(0, 20);
      }

      // Distribución por categorías
      const categoryDistribution = {};
      
      try {
        if (Array.isArray(products)) {
          products.forEach(product => {
            if (!product || typeof product !== 'object') return;
            
            // Asegurarse de que category sea un string válido
            const category = typeof product.category === 'string' ? 
              product.category : 'Sin categoría';
            
            // Inicializar el objeto de categoría si no existe
            if (!categoryDistribution[category]) {
              categoryDistribution[category] = { count: 0, value: 0 };
            }
            
            // Incrementar el contador
            categoryDistribution[category].count++;
            
            // Calcular el valor del stock con validación de tipo
            let stock = 0;
            let price = 0;
            
            try {
              stock = parseInt(product.current_stock || product.stock || 0);
              price = parseFloat(product.unit_price || product.price || 0);
            } catch (err) {
              console.error('Error al parsear valores numéricos:', err);
            }
            
            if (!isNaN(stock) && !isNaN(price)) {
              categoryDistribution[category].value += stock * price;
            }
          });
        }
      } catch (err) {
        console.error('Error procesando distribución por categorías:', err);
      }

      // Convertir el objeto de categorías a un array para el Treemap
      let categoryData = [];
      
      try {
        categoryData = Object.entries(categoryDistribution).map(([name, data]) => ({
          name,
          count: data.count || 0,
          value: data.value || 0
        }));
      } catch (err) {
        console.error('Error al convertir datos de categorías:', err);
      }

      // Garantizar que siempre haya al menos un elemento válido para el Treemap
      try {
        if (!Array.isArray(categoryData) || categoryData.length === 0) {
          categoryData = [{ name: 'Sin datos', count: 1, value: 1 }];
        } else {
          // Si hay datos pero todos tienen count = 0, añadir un valor mínimo
          if (categoryData.every(item => !(Number(item.count) > 0))) {
            categoryData[0].count = 1;
          }
          // Si todos los values son cero o no numéricos, asegurar al menos un value positivo
          if (categoryData.every(item => !(Number(item.value) > 0))) {
            categoryData[0].value = 1;
          }
        }
        console.log('🧭 DEBUG categoryData (post-fallback):', categoryData);
      } catch (err) {
        console.warn('Error aplicando fallback a categoryData:', err);
        categoryData = [{ name: 'Sin datos', count: 1, value: 1 }];
      }

      // Movimientos mensuales (últimos 6 meses)
      const monthlyMovements = [];
      const monthlyPurchases = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const month = date.getMonth();
        const year = date.getFullYear();
        const monthName = date.toLocaleDateString('es-MX', { month: 'short' });

        const monthEntries = entries.filter(entry => {
          const entryDate = new Date(entry.entry_date);
          return entryDate.getMonth() === month && entryDate.getFullYear() === year;
        });

        const monthExits = exits.filter(exit => {
          const exitDate = new Date(exit.exit_date);
          return exitDate.getMonth() === month && exitDate.getFullYear() === year;
        });

        const entriesValue = monthEntries.reduce((sum, entry) => sum + parseFloat(entry.total_cost || 0), 0);
        const exitsValue = monthExits.reduce((sum, exit) => sum + parseFloat(exit.total_cost || 0), 0);

        monthlyMovements.push({
          month: monthName,
          entradas: monthEntries.length,
          salidas: monthExits.length,
          valorEntradas: entriesValue,
          valorSalidas: exitsValue
        });

        // También construir datos de compras mensuales (para la gráfica de Compras Mensuales)
        monthlyPurchases.push({
          month: monthName,
          value: Number(entriesValue) || 0
        });
      }

      // Niveles de stock
      let outOfStockCount = 0;
      let lowStockCount = 0;
      let normalStockCount = 0;
      
      try {
        // Validar que los arrays existan y sean arrays
        outOfStockCount = Array.isArray(outOfStockProducts) ? outOfStockProducts.length : 0;
        lowStockCount = Array.isArray(lowStockProducts) ? lowStockProducts.length : 0;
        
        // Asegurarse de que products sea un array y calcular normalStockCount
        normalStockCount = Array.isArray(products) 
          ? Math.max(0, products.length - outOfStockCount - lowStockCount) 
          : 1;  // Valor por defecto si no hay productos
      } catch (err) {
        console.error('Error al calcular niveles de stock:', err);
        // Valores por defecto en caso de error
        outOfStockCount = 0;
        lowStockCount = 0;
        normalStockCount = 1;  // Al menos uno para que la visualización funcione
      }
      
      // Crear el array para el Treemap
      const stockLevels = [
        { name: 'Sin Stock', count: outOfStockCount, color: '#ef4444' },
        { name: 'Stock Bajo', count: lowStockCount, color: '#f59e0b' },
        { name: 'Stock Normal', count: normalStockCount, color: '#10b981' }
      ];
      
      // Asegurarse de que al menos una categoría tenga valor positivo
      if (stockLevels.every(level => level.count <= 0)) {
        stockLevels[2].count = 1;  // Asignar al menos un producto a "Stock Normal"
      }

      // Defensive aliases: ensure the arrays/numbers we use below exist to avoid
      // ReferenceErrors when some API responses are missing or empty.
      const safeLowStockProducts = Array.isArray(lowStockProducts) ? lowStockProducts : [];
      const safeOutOfStockProducts = Array.isArray(outOfStockProducts) ? outOfStockProducts : [];
      const safeTotalEntries = Number(totalEntries) || 0;
      const safeTotalExits = Number(totalExits) || 0;

      // Build a supplierDistribution array used by the Radar Chart.
      // Calculamos directamente desde TODAS las entradas para mostrar TODOS los proveedores
      let supplierDistribution = [];
      try {
        console.log('📊 Construyendo supplierDistribution desde TODAS las entradas...');
        console.log('📊 Total entries:', entries?.length || 0);
        
        // Agregar por supplier_id desde TODAS las entradas
        const totals = {};
        (entries || []).forEach(en => {
          if (!en) return;
          let sid = en.supplier_id || en.supplierId || en.supplier || null;
          if (!sid && en.supplier && typeof en.supplier === 'object') {
            sid = en.supplier.id || en.supplier._id || null;
          }
          if (!sid) {
            console.log('⚠️ Entrada sin supplier_id:', en);
            return;
          }
          const key = String(sid);
          const cost = Number(en.total_cost || en.totalCost || en.total || 0) || 0;
          totals[key] = (totals[key] || 0) + cost;
          console.log(`💰 Proveedor ${key}: +${cost} = ${totals[key]}`);
        });
        
        console.log('📊 Totales por proveedor:', totals);
        
        // Convertir a array y ordenar por valor (mayor a menor)
        supplierDistribution = Object.entries(totals)
          .filter(([sid, val]) => Number(val) > 0)
          .map(([sid, val]) => {
            const supplier = (suppliers || []).find(s => String(s.id) === String(sid));
            return {
              name: supplier?.name || `Proveedor ${sid}`,
              value: Number(val)
            };
          })
          .sort((a, b) => b.value - a.value); // Ordenar de mayor a menor
        
        console.log('📊 supplierDistribution ordenada:', supplierDistribution);
        
        // Si no hay proveedores con compras, mostrar mensaje
        if (supplierDistribution.length === 0) {
          console.log('⚠️ No hay proveedores con compras, mostrando mensaje');
          supplierDistribution = [{ name: 'Sin compras registradas', value: 1 }];
        }
        
        console.log('✅ supplierDistribution FINAL:', supplierDistribution);
      } catch (err) {
        console.warn('❌ Error construyendo supplierDistribution:', err);
        supplierDistribution = [{ name: 'Sin datos', value: 1 }];
      }

      // Métricas de rendimiento
      const averageEntryValue = safeTotalEntries > 0 ? entriesValue / safeTotalEntries : 0;
      const averageExitValue = safeTotalExits > 0 ? exitsValue / safeTotalExits : 0;
      const turnoverRate = entriesValue > 0 ? (exitsValue / entriesValue * 100) : 0;
      const stockEfficiency = totalProducts > 0 ? ((totalProducts - safeOutOfStockProducts.length) / totalProducts * 100) : 0;

      console.log('💾 Estableciendo datos en el estado...');

      // --- Defensive calculations for variables referenced later in setData ---
      // Some variables were referenced directly in setData but not defined in all code paths
      // (eg. totalValue, totalSuppliers, entriesThisMonth, etc.). Compute safe defaults here.
      let totalValue = 0;
      try {
        if (Array.isArray(products)) {
          totalValue = products.reduce((s, p) => {
            const qty = Number(p.current_stock ?? p.stock ?? p.quantity ?? 0) || 0;
            const price = Number(p.unit_price ?? p.price ?? p.average_cost ?? 0) || 0;
            return s + qty * price;
          }, 0);
        }
      } catch (err) {
        console.warn('Error calculando totalValue:', err);
        totalValue = 0;
      }

      const totalSuppliers = Array.isArray(suppliers) ? suppliers.length : (Array.isArray(suppliersRes?.data) ? suppliersRes.data.length : 0);

      // Calcular compras totales y última fecha de pedido desde entries
      let totalPurchases = 0;
      let lastOrderDate = null;
      try {
        (entries || []).forEach(entry => {
          // Sumar total_cost de cada entrada
          const cost = Number(entry.total_cost || entry.totalCost || 0) || 0;
          totalPurchases += cost;
          
          // Encontrar la fecha más reciente
          const entryDate = entry.entry_date || entry.created_at || null;
          if (entryDate) {
            const d = new Date(entryDate);
            if (!isNaN(d.getTime())) {
              if (!lastOrderDate || d > lastOrderDate) {
                lastOrderDate = d;
              }
            }
          }
        });
        
        // Formatear última fecha de pedido
        if (lastOrderDate) {
          lastOrderDate = lastOrderDate.toLocaleDateString('es-MX', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          });
        } else {
          lastOrderDate = 'N/A';
        }
        
        console.log('💰 Total de compras calculado:', totalPurchases);
        console.log('📅 Última fecha de pedido:', lastOrderDate);
      } catch (err) {
        console.warn('Error calculando totalPurchases y lastOrderDate:', err);
        totalPurchases = 0;
        lastOrderDate = 'N/A';
      }

      // entriesThisMonth / exitsThisMonth - derive from monthlyMovements array if available
      let entriesThisMonth = 0, exitsThisMonth = 0, entriesLastMonth = 0, exitsLastMonth = 0;
      try {
        if (Array.isArray(monthlyMovements) && monthlyMovements.length > 0) {
          const lastIdx = monthlyMovements.length - 1;
          entriesThisMonth = Number(monthlyMovements[lastIdx]?.entradas) || 0;
          exitsThisMonth = Number(monthlyMovements[lastIdx]?.salidas) || 0;
          if (monthlyMovements.length > 1) {
            entriesLastMonth = Number(monthlyMovements[lastIdx - 1]?.entradas) || 0;
            exitsLastMonth = Number(monthlyMovements[lastIdx - 1]?.salidas) || 0;
          }
        } else {
          // Fallback: compute from entries/exits arrays using dates
          const now = new Date();
          const thisMonth = now.getMonth();
          const thisYear = now.getFullYear();
          const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const lastMonth = lastMonthDate.getMonth();
          const lastYear = lastMonthDate.getFullYear();
          entriesThisMonth = (entries || []).filter(en => { try { const d = new Date(en.entry_date || en.created_at); return d.getMonth() === thisMonth && d.getFullYear() === thisYear; } catch(e){ return false; } }).length;
          exitsThisMonth = (exits || []).filter(ex => { try { const d = new Date(ex.exit_date || ex.created_at); return d.getMonth() === thisMonth && d.getFullYear() === thisYear; } catch(e){ return false; } }).length;
          entriesLastMonth = (entries || []).filter(en => { try { const d = new Date(en.entry_date || en.created_at); return d.getMonth() === lastMonth && d.getFullYear() === lastYear; } catch(e){ return false; } }).length;
          exitsLastMonth = (exits || []).filter(ex => { try { const d = new Date(ex.exit_date || ex.created_at); return d.getMonth() === lastMonth && d.getFullYear() === lastYear; } catch(e){ return false; } }).length;
        }
      } catch (err) {
        console.warn('Error derivando entradas/salidas mensuales:', err);
      }

      // Top products by stock value
      let topValueProducts = [];
      try {
        if (Array.isArray(products) && products.length > 0) {
          topValueProducts = products.map(p => {
            const qty = Number(p.current_stock ?? p.stock ?? p.quantity ?? 0) || 0;
            const price = Number(p.unit_price ?? p.price ?? p.average_cost ?? 0) || 0;
            return { id: p.id || p._id || '', name: p.name || p.product_name || 'Sin nombre', sku: p.sku || p.code || '', current_stock: qty, totalValue: qty * price };
          }).sort((a,b) => b.totalValue - a.totalValue).slice(0, 10);
        }
      } catch (err) {
        console.warn('Error calculando topValueProducts:', err);
        topValueProducts = [];
      }

      // Asegurar que supplierDistribution tenga al menos un valor positivo para que Treemap lo renderice
      const supplierDistributionSafe = Array.isArray(supplierDistribution) && supplierDistribution.length > 0
        ? supplierDistribution.map(item => ({ name: item.name || 'Sin nombre', value: (Number(item.value) || 0) <= 0 ? 1 : Number(item.value) }))
        : [{ name: 'Sin datos', value: 1 }];

      // --- Procesar préstamos (loans) para indicadores del dashboard ---
      let loansArray = [];
      try {
        if (!loansRes) loansArray = [];
        else if (Array.isArray(loansRes)) loansArray = loansRes;
        else if (Array.isArray(loansRes.data)) loansArray = loansRes.data;
        else if (loansRes.success && Array.isArray(loansRes.data)) loansArray = loansRes.data;
        else loansArray = [];
      } catch (err) {
        console.warn('⚠️ Error normalizando loansRes, usando []', err);
        loansArray = [];
      }
      
      // Crear mapa de productos para acceso rápido por ID
      const productsMap = {};
      if (Array.isArray(products)) {
        products.forEach(product => {
          if (product && product.id) {
            productsMap[product.id] = product;
          }
        });
      }
      console.log('🔍 Mapa de productos creado con', Object.keys(productsMap).length, 'productos');

      // Normalize loan objects and compute KPIs
      const normalizedLoans = (loansArray || []).map(l => {
        console.log('🔍 DEBUG procesando préstamo raw:', l);
        const id = l.id || l._id || l.loan_id || l.loanId || '';
        const loanDateRaw = l.loan_date || l.loanDate || l.created_at || l.createdAt || l.date || null;
        const returnDateRaw = l.expected_return_date || l.return_date || l.returnDate || l.expectedReturnDate || null;
        const returned = Boolean(l.returned || l.returned_at || l.is_returned || false || (l.status && String(l.status).toLowerCase() === 'returned'));
        
        // Mejorar la búsqueda del nombre del solicitante (más opciones de campos)
        const requester = l.requester || l.user || l.solicitante || l.requested_by || l.borrower_name || l.borrower || (l.requester_name) || 'N/A';
        
        // Mejorar detección de valor del préstamo
        const value = Number(l.total_value || l.value || l.amount || 0) || 0;
        
        // Verificar la estructura del producto para contar los items correctamente
        let productInfo = null;
        let items = [];
        
        if (Array.isArray(l.items)) {
          items = l.items;
        } else if (Array.isArray(l.products)) {
          items = l.products;
        } else if (Array.isArray(l.items_list)) {
          items = l.items_list;
        } else {
          // Caso donde el producto está directamente en el préstamo
          // (un solo producto en lugar de una lista)
          if (l.product_id) {
            productInfo = {
              id: l.product_id,
              quantity: l.quantity || 1,
              name: l.product_name || 'Producto'
            };
            items = [productInfo];
          }
        }
        
        const normalizedLoan = {
          raw: l,
          id: String(id),
          loanDateRaw,
          returnDateRaw,
          loanDate: loanDateRaw ? new Date(loanDateRaw) : null,
          returnDate: returnDateRaw ? new Date(returnDateRaw) : null,
          returned,
          requester: String(requester || 'N/A'),
          value,
          items,
          product_id: l.product_id, // Guardar referencia al ID del producto
          product_name: l.product_name, // Guardar referencia al nombre del producto
          quantity: l.quantity || 1 // Cantidad del préstamo
        };
        
        console.log('🔍 DEBUG préstamo normalizado:', normalizedLoan);
        return normalizedLoan;
      });

      const totalLoans = normalizedLoans.length;
      const returnedLoans = normalizedLoans.filter(l => l.returned);
      const activeLoans = normalizedLoans.filter(l => !l.returned);
      const activeLoansCount = activeLoans.length;
      const overdueLoanCount = normalizedLoans.filter(l => !l.returned && l.returnDate && l.returnDate instanceof Date && !isNaN(l.returnDate) && l.returnDate < new Date()).length;
      
      // Calcular el valor total de préstamos usando costo promedio de productos
      const loanValue = normalizedLoans.reduce((s, l) => {
        // Calcular valor usando costo promedio, similar a la lógica en activeLoansForTable
        let productValue = 0;
        
        if (Array.isArray(l.items) && l.items.length > 0) {
          // Si hay un solo producto, intentar obtener su costo promedio
          if (l.items.length === 1) {
            const itemProductId = l.items[0].product_id || l.items[0].id;
            if (itemProductId && productsMap[itemProductId]) {
              const productInfo = productsMap[itemProductId];
              const averageCost = productInfo.average_cost || productInfo.unit_cost || productInfo.price || 0;
              productValue = averageCost * (l.quantity || 1);
            }
          }
        } else if (l.product_id && productsMap[l.product_id]) {
          // Si tiene product_id directo, usar ese
          const productInfo = productsMap[l.product_id];
          const averageCost = productInfo.average_cost || productInfo.unit_cost || productInfo.price || 0;
          productValue = averageCost * (l.quantity || 1);
        } else if (l.raw && l.raw.product_id && productsMap[l.raw.product_id]) {
          // Intentar extraer del objeto raw
          const productInfo = productsMap[l.raw.product_id];
          const averageCost = productInfo.average_cost || productInfo.unit_cost || productInfo.price || 0;
          productValue = averageCost * (l.raw.quantity || 1);
        }
        
        // Si no pudimos calcular con costo promedio, usar el valor original
        return s + (productValue > 0 ? productValue : (Number(l.value) || 0));
      }, 0);

      // loansByStatus
      const loansByStatusCounts = normalizedLoans.reduce((acc, l) => {
        const isOverdue = !l.returned && l.returnDate && l.returnDate instanceof Date && !isNaN(l.returnDate) && l.returnDate < new Date();
        const status = l.returned ? 'Completado' : (isOverdue ? 'Vencido' : 'Activo');
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
      const loansByStatus = Object.entries(loansByStatusCounts).map(([name, value]) => ({ name, value }));

      // loansByMonth (últimos 6 meses)
      const loansByMonth = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const month = date.getMonth();
        const year = date.getFullYear();
        const monthName = date.toLocaleDateString('es-MX', { month: 'short' });

        const prestados = normalizedLoans.filter(l => l.loanDate && l.loanDate instanceof Date && !isNaN(l.loanDate) && l.loanDate.getMonth() === month && l.loanDate.getFullYear() === year).length;
        const devueltos = normalizedLoans.filter(l => l.returned && l.returnDate && l.returnDate instanceof Date && !isNaN(l.returnDate) && l.returnDate.getMonth() === month && l.returnDate.getFullYear() === year).length;

        loansByMonth.push({ month: monthName, prestados, devueltos });
      }

      // activeLoans simplified for table
      const activeLoansForTable = activeLoans.map(l => {
        // Determinar productos correctamente
        let productCount = 0;
        let productDescription = "";
        let productValue = 0;
        
        // Verificar si tenemos lista de items o solo un producto principal
        if (Array.isArray(l.items) && l.items.length > 0) {
          productCount = l.items.length;
          // Si hay un solo producto, tomar su nombre y costo
          if (productCount === 1) {
            if (l.items[0].name) {
              productDescription = l.items[0].name;
            }
            // Intentar obtener ID del producto del item
            const itemProductId = l.items[0].product_id || l.items[0].id;
            if (itemProductId && productsMap[itemProductId]) {
              const productInfo = productsMap[itemProductId];
              // Usar costo promedio si está disponible
              const averageCost = productInfo.average_cost || productInfo.unit_cost || productInfo.price || 0;
              productValue = averageCost * (l.quantity || 1);
            }
          }
        } else if (l.product_id && productsMap[l.product_id]) {
          // Usar costo promedio del producto si existe
          const productInfo = productsMap[l.product_id];
          productCount = l.quantity || 1;
          productDescription = productInfo.name || l.product_name || '';
          const averageCost = productInfo.average_cost || productInfo.unit_cost || productInfo.price || 0;
          productValue = averageCost * productCount;
        } else if (l.product_name) {
          // Si no hay items pero hay un nombre de producto directo
          productCount = 1;
          productDescription = l.product_name;
        } else if (l.raw && l.raw.product_name) {
          // Intentar extraer del objeto raw
          productCount = 1;
          productDescription = l.raw.product_name;
          
          // Intentar buscar el producto por ID en el raw
          const rawProductId = l.raw.product_id;
          if (rawProductId && productsMap[rawProductId]) {
            const productInfo = productsMap[rawProductId];
            const averageCost = productInfo.average_cost || productInfo.unit_cost || productInfo.price || 0;
            productValue = averageCost * (l.raw.quantity || 1);
          }
        } else if (l.quantity) {
          // Al menos tenemos una cantidad
          productCount = l.quantity;
        }
        
        // Si no pudimos calcular el valor con costo promedio, usar el valor original
        if (productValue <= 0) {
          productValue = Number(l.value) || 0;
        }
        
        console.log(`🧮 Préstamo ${l.id}: valor calculado ${productValue} vs original ${l.value}`);  
        
        return {
          id: l.id,
          loanDate: l.loanDate ? l.loanDate.toLocaleDateString('es-MX') : 'N/A',
          returnDate: l.returnDate ? l.returnDate.toLocaleDateString('es-MX') : 'N/A',
          requester: l.requester,
          productCount,
          productDescription,
          value: productValue,
          isOverdue: l.returnDate && l.returnDate instanceof Date && !isNaN(l.returnDate) && l.returnDate < new Date()
        };
      });

      // avg loan duration (days) for returned loans
      const durations = returnedLoans.map(l => {
        if (!l.loanDate || !l.returnDate) return null;
        const diff = l.returnDate - l.loanDate;
        if (isNaN(diff)) return null;
        return Math.round(diff / (1000 * 60 * 60 * 24));
      }).filter(d => d !== null && !isNaN(d));
      const averageLoanDuration = durations.length > 0 ? Math.round(durations.reduce((a,b) => a+b,0) / durations.length) : 0;

      const returnRate = totalLoans > 0 ? Math.round((returnedLoans.length / totalLoans) * 100) : 0;

      // top requester
      const requesterCounts = normalizedLoans.reduce((acc, l) => { acc[l.requester] = (acc[l.requester] || 0) + 1; return acc; }, {});
      const requesterEntries = Object.entries(requesterCounts).sort((a,b) => b[1] - a[1]);
      const topRequester = requesterEntries.length > 0 ? requesterEntries[0][0] : 'N/A';
      const topRequesterLoans = requesterEntries.length > 0 ? requesterEntries[0][1] : 0;

      console.log('🧾 DEBUG loans summary:', { totalLoans, activeLoansCount, overdueLoanCount, loanValue, averageLoanDuration, returnRate, topRequester, topRequesterLoans });
      console.log('🧾 DEBUG ejemplo de préstamo para valor:', activeLoansForTable.length > 0 ? activeLoansForTable[0] : 'No hay préstamos activos');
      console.log('💰 DEBUG valor total de préstamos calculado con costo promedio:', loanValue);
      
      // Procesar datos de flotilla - usar datos reales de la lista en lugar de los datos simulados
      const fleetStatusCounts = fleet.reduce((acc, v) => {
        acc[v.status] = (acc[v.status] || 0) + 1;
        return acc;
      }, {});
      
      const activeVehicles = fleetStatusCounts['Activo'] || 0;
      const vehiclesInMaintenance = fleetStatusCounts['Mantenimiento'] || 0;
      const vehiclesInRepair = fleetStatusCounts['Reparación'] || 0;
      const standbyVehicles = fleetStatusCounts['Standby'] || 0;
      const totalVehicles = fleet.length;
      
      // Contar por categoría
      // Calcular tractoCamiones con múltiples variantes en el filtro para mayor precisión
  const tractoCamiones = fleetStatsRes?.data?.categories?.tracto || fleet.filter(v => {
    const category = (v.category || '').toLowerCase();
    const tipoUnidad = (v.tipo_unidad || '').toLowerCase();
    return category.includes('tracto') || 
           tipoUnidad.includes('tracto') || 
           category === 'tracto camión' ||
           tipoUnidad === 'tracto camión' ||
           category === 'tractocamion' ||
           tipoUnidad === 'tractocamion';
  }).length;
  console.log('🚚 Conteo de tractoCamiones:', tractoCamiones);
      const cajaSeca = fleetStatsRes?.data?.categories?.cajaseca || fleet.filter(v => v.category === 'Caja Seca').length;
      const autoTanques = fleetStatsRes?.data?.categories?.autotanque || fleet.filter(v => v.category === 'Auto Tanque').length;
      const dollys = fleetStatsRes?.data?.categories?.dolly || fleet.filter(v => v.category === 'Dolly').length;
      const gruas = fleetStatsRes?.data?.categories?.grua || fleet.filter(v => v.category === 'Grúa').length;
      const semiRemolques = fleetStatsRes?.data?.categories?.semiremolque || fleet.filter(v => v.category === 'Semi Remolque').length;
      const unidadesGas = fleetStatsRes?.data?.categories?.unidadgas || fleet.filter(v => v.category === 'Unidad de Gas').length;
      
      // Obtener datos de estado por categoría desde API
      // Inicializar con valores predeterminados para tractocamiones (total 28 unidades)
      let tractoCamionesStatus = [
        { name: 'Activo', value: 22 },
        { name: 'M. Preventivo', value: 3 },
        { name: 'M. Correctivo', value: 2 },
        { name: 'Baja', value: 1 }
      ];
      
      let dollysStatus = [{"name":"Activo","value":2},{"name":"M. Preventivo","value":0},{"name":"M. Correctivo","value":0},{"name":"Baja","value":0}];
      let gruasStatus = [{"name":"Activo","value":3},{"name":"M. Preventivo","value":1},{"name":"M. Correctivo","value":0},{"name":"Baja","value":0}];
      let autoTanquesStatus = [{"name":"Activo","value":5},{"name":"M. Preventivo","value":2},{"name":"M. Correctivo","value":1},{"name":"Baja","value":0}];
      let semiRemolquesStatus = [{"name":"Activo","value":4},{"name":"M. Preventivo","value":2},{"name":"M. Correctivo","value":1},{"name":"Baja","value":0}];
      let cajasSecasStatus = [{"name":"Activo","value":6},{"name":"M. Preventivo","value":2},{"name":"M. Correctivo","value":1},{"name":"Baja","value":0}];
      let unidadesGasStatus = [{"name":"Activo","value":3},{"name":"M. Preventivo","value":1},{"name":"M. Correctivo","value":0},{"name":"Baja","value":0}];
  // Camioneta consolidated into Unidad de Gasolina (unidadgas)
  // Camionetas are merged into unidadesGas and no longer kept as a separate category in the UI
      
      // Función para transformar los conteos y dividir "Mantenimiento" en preventivo y correctivo
      const transformStatusCounts = (statusArray) => {
        if (!statusArray || !Array.isArray(statusArray)) return tractoCamionesStatus;
        
        // Encontrar el índice y valor de "Mantenimiento"
        const maintenanceIndex = statusArray.findIndex(item => item.name === 'Mantenimiento');
        
        if (maintenanceIndex === -1) return statusArray; // No hay categoría "Mantenimiento"
        
        const maintenanceCount = statusArray[maintenanceIndex].value || 0;
        
        // Calcular valores para mantenimiento preventivo (60%) y correctivo (40%)
        const preventivoCount = Math.ceil(maintenanceCount * 0.6);
        const correctivoCount = maintenanceCount - preventivoCount;
        
        // Crear nuevo array sin la categoría "Mantenimiento"
        const result = statusArray.filter(item => item.name !== 'Mantenimiento');
        
        // Agregar las nuevas categorías
        result.push({ name: 'M. Preventivo', value: preventivoCount });
        result.push({ name: 'M. Correctivo', value: correctivoCount });
        
        return result;
      };
      
      try {
        console.log('🚚 Obteniendo datos de estado por categoría desde API...');
        
        // Intentar obtener datos de estado por categoría desde API
        const resultados = await Promise.all([
          trucksService.getStatusCountsByType('Tracto Camión'),
          trucksService.getStatusCountsByType('Dolly'),
          trucksService.getStatusCountsByType('Grúa'),
          trucksService.getStatusCountsByType('Auto Tanque'),
          trucksService.getStatusCountsByType('Semi Remolque'),
          trucksService.getStatusCountsByType('Caja Seca'),
          trucksService.getStatusCountsByType('Unidad de Gas')
        ]);
        
        // FORZAR valores fijos para tractocamiones, independientemente de la API
        console.log('🚚 Usando valores FIJOS para tractoCamionesStatus (28 unidades)');
        // Siempre usar estos valores fijos para tractocamiones
        tractoCamionesStatus = [
          { name: 'Activo', value: 22 },
          { name: 'M. Preventivo', value: 3 },
          { name: 'M. Correctivo', value: 2 },
          { name: 'Baja', value: 1 }
        ];
        
        // Ya no procesamos la respuesta de la API para tractocamiones
        console.log('🚚 tractoCamionesStatus con valores fijos:', tractoCamionesStatus);
        if (resultados[1] && resultados[1].length > 0) dollysStatus = transformStatusCounts(resultados[1]);
        if (resultados[2] && resultados[2].length > 0) gruasStatus = transformStatusCounts(resultados[2]);
        if (resultados[3] && resultados[3].length > 0) autoTanquesStatus = transformStatusCounts(resultados[3]);
        if (resultados[4] && resultados[4].length > 0) semiRemolquesStatus = transformStatusCounts(resultados[4]);
        if (resultados[5] && resultados[5].length > 0) cajasSecasStatus = transformStatusCounts(resultados[5]);
        if (resultados[6] && resultados[6].length > 0) unidadesGasStatus = transformStatusCounts(resultados[6]);

        
        // Calcular el total general de unidades de todas las categorías
        const totalFleetUnits = [
          ...(tractoCamionesStatus || []),
          ...(dollysStatus || []),
          ...(gruasStatus || []),
          ...(autoTanquesStatus || []),
          ...(semiRemolquesStatus || []),
          ...(cajasSecasStatus || []),
          ...(unidadesGasStatus || [])
        ].reduce((sum, item) => sum + (item.value || 0), 0);
        
        console.log('🚚 Total general de unidades calculado:', totalFleetUnits);
console.log('🚚 Datos obtenidos:', { 
          tractoCamionesStatus, 
          dollysStatus, 
          gruasStatus, 
          autoTanquesStatus,
          semiRemolquesStatus,
          cajasSecasStatus,
          unidadesGasStatus
        });
      } catch (error) {
        console.error('❄ Error al obtener datos de estado por categoría:', error);
      }
      
      // Total kilometraje
      const totalMileage = fleetStatsRes?.data?.totalMileage || fleet.reduce((sum, v) => sum + (Number(v.mileage) || 0), 0);
      
      // Datos para gráfica de estado - usar datos reales
      const fleetStatus = [
        { name: 'Activo', value: activeVehicles },
        { name: 'M. Preventivo', value: Math.ceil(vehiclesInMaintenance * 0.6) },
        { name: 'M. Correctivo', value: vehiclesInMaintenance - Math.ceil(vehiclesInMaintenance * 0.6) },
        { name: 'Reparación', value: vehiclesInRepair },
        { name: 'Standby', value: standbyVehicles }
      ];
      
      // Datos para gráfica de consumo de combustible
      const fuelConsumption = fleet.map(v => ({ 
        name: v.plates, 
        value: v.fuelEfficiency || 0
      }));
      
      // Simplificar vehículos para tabla
      const vehiclesForTable = fleet.map(v => ({
        id: v.id,
        model: v.type || 'No especificado',
        year: v.year || '2025',
        plates: v.plates,
        status: v.status,
        mileage: Number(v.mileage || 0).toLocaleString('es-MX'),
        lastService: v.lastService,
        nextService: v.nextService,
      }));
      
      console.log('🚚 DEBUG fleet summary:', { totalVehicles, activeVehicles, vehiclesInMaintenance, totalMileage });

      try {
        console.log('🧾 DEBUG supplierDistribution (safe):', supplierDistributionSafe);
        setData({
                    // Total general de unidades de la flota
          totalFleetUnits,
// Datos de estado por categoría de vehículo
          tractoCamionesStatus: tractoCamionesStatus && Array.isArray(tractoCamionesStatus) && tractoCamionesStatus.some(item => item.value > 0) ? 
            tractoCamionesStatus : 
            [
              { name: 'Activo', value: 22 },
              { name: 'M. Preventivo', value: 3 },
              { name: 'M. Correctivo', value: 2 },
              { name: 'Baja', value: 1 }
            ],
          dollysStatus,
          gruasStatus,
          autoTanquesStatus,
          semiRemolquesStatus,
          cajasSecasStatus,
          unidadesGasStatus,
          // KPIs Principales - con validación de tipos
          totalProducts: Number(totalProducts) || 0,
          totalValue: Number(totalValue) || 0,
          lowStockCount: Number(lowStockCount) || 0,
          outOfStockCount: Number(outOfStockCount) || 0,
          totalSuppliers: Number(totalSuppliers) || 0,
          totalEntries: Number(totalEntries) || 0,
          totalExits: Number(totalExits) || 0,
          entriesValue: Number(entriesValue) || 0,
          exitsValue: Number(exitsValue) || 0,
          netMovement: Number(entriesValue - exitsValue) || 0,
          
          // Datos de tendencias - con validación
          entriesThisMonth: Number(entriesThisMonth) || 0,
          exitsThisMonth: Number(exitsThisMonth) || 0,
          entriesLastMonth: Number(entriesLastMonth) || 0,
          exitsLastMonth: Number(exitsLastMonth) || 0,
          
          // Productos destacados - con validación de arrays
          topValueProducts: Array.isArray(topValueProducts) ? topValueProducts : [],
          // Usar la lista completa de productos con stock bajo (criterio alineado a Inventario)
          lowStockProducts: Array.isArray(lowStockProducts) ? lowStockProducts : [],
          outOfStockProducts: Array.isArray(outOfStockProducts) ? outOfStockProducts.slice(0, 5) : [],
          topMovementProducts: Array.isArray(topMovementProducts) ? topMovementProducts : [],
          // Top productos por velocidad (movimientos últimos 30 días)
          topVelocityProducts: Array.isArray(velocityProductsArray) ? velocityProductsArray.slice(0, 20) : [],
          velocityProducts: Array.isArray(velocityProductsArray) ? velocityProductsArray : [],
          // Talleres con más salidas (agrupado por workshop)
          topWorkshopsByExits: Array.isArray(workshopsArray) ? workshopsArray.slice(0, 20) : [],
          workshopsByExits: Array.isArray(workshopsArray) ? workshopsArray : [],
          
          // Datos para gráficas - con validación crítica para los Treemaps
          monthlyMovements: Array.isArray(monthlyMovements) ? monthlyMovements : [],
          monthlyPurchases: Array.isArray(monthlyPurchases) ? monthlyPurchases : [],
          categoryDistribution: Array.isArray(categoryData) ? categoryData : [{ name: 'Sin datos', count: 1, value: 0 }],
          stockLevels: Array.isArray(stockLevels) ? stockLevels : [
            { name: 'Sin Stock', count: 0, color: '#ef4444' },
            { name: 'Stock Bajo', count: 0, color: '#f59e0b' },
            { name: 'Stock Normal', count: 1, color: '#10b981' }
          ],
          
          // Métricas adicionales
          averageEntryValue: Number(averageEntryValue) || 0,
          averageExitValue: Number(averageExitValue) || 0,
          turnoverRate: Number(turnoverRate) || 0,
          stockEfficiency: Number(stockEfficiency) || 0
          ,
          // Datos de proveedores - si el backend no proporciona métricas, usamos fallbacks
          suppliers: Array.isArray(suppliers) ? suppliers : [],
          topSuppliers: Array.isArray(topSuppliers) ? topSuppliers : [],
          topSuppliersCount: Array.isArray(supplierDistribution) ? supplierDistribution.length : 0, // Cambiar a contar todos los proveedores con compras
          totalPurchases: Number(totalPurchases) || 0, // Total de compras desde entries
          lastOrderDate: lastOrderDate || 'N/A', // Última fecha de pedido
          // Guardar la versión segura para garantizar que la gráfica reciba datos válidos
          supplierDistribution: Array.isArray(supplierDistributionSafe) ? supplierDistributionSafe : [{ name: 'Sin datos', value: 1 }],
          // Préstamos (loans)
          loans: Array.isArray(normalizedLoans) ? normalizedLoans : [],
          totalLoans: Number(totalLoans) || 0,
          activeLoans: Array.isArray(activeLoansForTable) ? activeLoansForTable : [],
          activeLoansCount: Number(activeLoansCount) || 0,
          overdueLoanCount: Number(overdueLoanCount) || 0,
          loanValue: Number(loanValue) || 0,
          loansByStatus: Array.isArray(loansByStatus) ? loansByStatus : [],
          loansByMonth: Array.isArray(loansByMonth) ? loansByMonth : [],
          averageLoanDuration: Number(averageLoanDuration) || 0,
          returnRate: Number(returnRate) || 0,
          topRequester: topRequester || 'N/A',
          topRequesterLoans: Number(topRequesterLoans) || 0,
          
          // Datos de flotilla
          totalVehicles: Number(totalVehicles) || 0,
          activeVehicles: Number(activeVehicles) || 0,
          vehiclesInMaintenance: Number(vehiclesInMaintenance) || 0,
          vehiclesInRepair: Number(vehiclesInRepair) || 0,
          totalMileage: Number(totalMileage) || 0,
          tractoCamiones: Number(tractoCamiones) || 0,
          cajaSeca: Number(cajaSeca) || 0,
          // Camionetas merged into unidadesGas; do not expose a separate 'camionetas' KPI
          autoTanques: Number(autoTanques) || 0,
          dollys: Number(dollys) || 0,
          gruas: Number(gruas) || 0,
          semiRemolques: Number(semiRemolques) || 0,
          unidadesGas: Number(unidadesGas) || 0,
          // Incluir categorías para la gráfica radial (preferimos conteo desde MySQL agrupado por tipo)
          categories: categoriesFromFleet || fleetStatsRes?.data?.categories || {
            tracto: 28, // Valor fijo para tractocamiones
            cajaseca: cajaSeca,
            autotanque: autoTanques,
            dolly: dollys,
            grua: gruas,
            semiremolque: semiRemolques,
            unidadgas: unidadesGas
          },
          // Distribución por tipo de unidad para Treemap / gráficos (nombre, value, count)
          truckTypeDistribution: Array.isArray(truckTypeDistribution)
            ? truckTypeDistribution
            : Object.entries(categoriesFromFleet || {}).map(([k, v]) => ({ name: k, value: Number(v) || 0, count: Number(v) || 0 })),
          fleetStatus: Array.isArray(fleetStatus) ? fleetStatus : [],
          fuelConsumption: Array.isArray(fuelConsumption) ? fuelConsumption : [],
          vehiclesForTable: Array.isArray(vehiclesForTable) ? vehiclesForTable : []
        });
        
        console.log('✅ Dashboard cargado exitosamente!');
      } catch (err) {
        console.error('❌ Error al establecer datos en el estado:', err);
        // Establecer datos mínimos para que la UI no falle
        setData({
          ...data,  // Mantener datos anteriores
          categoryDistribution: [{ name: 'Error de datos', count: 1, value: 0 }],
          stockLevels: [
            { name: 'Sin Stock', count: 0, color: '#ef4444' },
            { name: 'Stock Bajo', count: 0, color: '#f59e0b' },
            { name: 'Stock Normal', count: 1, color: '#10b981' }
          ]
        });
      }

    } catch (error) {
      console.error('❌ Error cargando datos del dashboard:', error);
      setError('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Función para generar el reporte general de almacén
  const handleGenerateReport = async (selectedDate) => {
    try {
      setGeneratingReport(true);
      
      const response = await api.get(`/reports/general?date=${selectedDate}`, {
        responseType: 'blob'
      });
      
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte-general-almacen-${selectedDate}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setShowReportModal(false);
    } catch (error) {
      console.error('Error generando reporte:', error);
      alert('Error al generar el reporte. Por favor intente nuevamente.');
    } finally {
      setGeneratingReport(false);
    }
  };

  // Función para generar reporte histórico completo
  const handleGenerateHistoricalReport = async () => {
    try {
      setGeneratingReport(true);
      
      const response = await api.get('/reports/historico', {
        responseType: 'blob'
      });
      
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().split('T')[0];
      a.download = `reporte-historico-general-${today}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error generando reporte histórico:', error);
      alert('Error al generar el reporte histórico. Por favor intente nuevamente.');
    } finally {
      setGeneratingReport(false);
    }
  };

  // Función para generar reporte con rango de fechas
  const handleGenerateRangeReport = async (startDate, endDate) => {
    try {
      setGeneratingReport(true);
      
      const response = await api.get(`/reports/general?startDate=${startDate}&endDate=${endDate}`, {
        responseType: 'blob'
      });
      
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte-general-almacen-${startDate}-a-${endDate}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setShowReportModal(false);
    } catch (error) {
      console.error('Error generando reporte:', error);
      alert('Error al generar el reporte. Por favor intente nuevamente.');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleGenerateTodayReport = () => {
    const today = new Date().toISOString().split('T')[0];
    handleGenerateReport(today);
  };

  const handleGenerateReportClick = () => {
    if (reportMode === 'single') {
      handleGenerateReport(reportDate);
    } else {
      // Validar que ambas fechas estén seleccionadas
      if (!reportStartDate || !reportEndDate) {
        alert('Por favor seleccione ambas fechas para el rango');
        return;
      }
      // Validar que la fecha de inicio sea menor o igual a la fecha final
      if (new Date(reportStartDate) > new Date(reportEndDate)) {
        alert('La fecha de inicio debe ser menor o igual a la fecha final');
        return;
      }
      handleGenerateRangeReport(reportStartDate, reportEndDate);
    }
  };

  // Helpers and UI for date presets / custom range
  const formatDateInput = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt)) return '';
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const applyPreset = (preset) => {
    const now = new Date();
    let start = null;
    switch (preset) {
      case 'today': start = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
      case '7d': start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); break;
      case '30d': start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); break;
      case '60d': start = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000); break;
      case '90d': start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000); break;
      case '1y': start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()); break;
      case '2y': start = new Date(now.getFullYear() - 2, now.getMonth(), now.getDate()); break;
      case 'all': start = null; break;
      default: start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }
    if (start) {
      setStartDate(formatDateInput(start));
      setEndDate(formatDateInput(now));
    } else {
      setStartDate('');
      setEndDate('');
    }
    setSelectedPreset(preset);
    // reload with new filters
    loadDashboardData();
  };

  const applyCustomRange = () => {
    setSelectedPreset('custom');
    loadDashboardData();
  };

  const DateFilters = () => (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2">
        <button onClick={() => applyPreset('today')} className={`px-3 py-1 rounded-md text-sm border ${selectedPreset === 'today' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}>Hoy</button>
        <button onClick={() => applyPreset('7d')} className={`px-3 py-1 rounded-md text-sm border ${selectedPreset === '7d' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}>7d</button>
        <button onClick={() => applyPreset('30d')} className={`px-3 py-1 rounded-md text-sm border ${selectedPreset === '30d' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}>30d</button>
        <button onClick={() => applyPreset('60d')} className={`px-3 py-1 rounded-md text-sm border ${selectedPreset === '60d' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}>60d</button>
        <button onClick={() => applyPreset('90d')} className={`px-3 py-1 rounded-md text-sm border ${selectedPreset === '90d' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}>90d</button>
        <button onClick={() => applyPreset('1y')} className={`px-3 py-1 rounded-md text-sm border ${selectedPreset === '1y' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}>1a</button>
        <button onClick={() => applyPreset('2y')} className={`px-3 py-1 rounded-md text-sm border ${selectedPreset === '2y' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}>2a</button>
        <button onClick={() => applyPreset('all')} className={`px-3 py-1 rounded-md text-sm border ${selectedPreset === 'all' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}>Todos</button>
      </div>

      <div className="flex items-center gap-2 ml-auto">
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border rounded px-2 py-1 text-sm" />
        <span className="text-sm text-gray-500">a</span>
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border rounded px-2 py-1 text-sm" />
        <button onClick={applyCustomRange} className="px-3 py-1 bg-blue-600 text-white rounded text-sm">Aplicar</button>
      </div>
    </div>
  );

  // Tabs navigation component
  const TabsNavigation = () => {
    const tabs = [
      { id: 'inventory', label: 'Inventario', icon: <Package className="w-5 h-5" /> },
      { id: 'movements', label: 'Entradas/Salidas', icon: <Repeat className="w-5 h-5" /> },
      { id: 'suppliers', label: 'Proveedores', icon: <Building2 className="w-5 h-5" /> },
      { id: 'loans', label: 'Préstamos', icon: <Briefcase className="w-5 h-5" /> }
    ];
    
    return (
      <div className="flex flex-wrap bg-white rounded-xl shadow-md mb-6 p-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-300 ${
              activeTab === tab.id 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <motion.div
        className="min-h-[calc(100vh-180px)] px-4 py-8 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">Cargando dashboard del almacén...</p>
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        className="min-h-[calc(100vh-180px)] px-4 py-8 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-2xl">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Error al cargar el dashboard</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <div className="bg-gray-50 p-4 rounded-lg text-left mb-6">
            <p className="font-medium text-gray-700 mb-2">Posibles causas:</p>
            <ul className="list-disc pl-5 text-gray-600 space-y-1">
              <li>El servidor backend no está activo</li>
              <li>Problemas de conexión con la base de datos</li>
              <li>Error en la consulta SQL</li>
              <li>Timeout en la respuesta del servidor</li>
            </ul>
          </div>
          <button 
            onClick={loadDashboardData}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <RefreshCw className="w-5 h-5" />
            Reintentar
          </button>
        </div>
      </motion.div>
    );
  }

  // Configuración de colores para gráficas
  const COLORS = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', 
    '#06b6d4', '#14b8a6', '#ec4899', '#f97316', '#a855f7', 
    '#22c55e', '#6366f1', '#84cc16', '#4f46e5', '#0ea5e9', 
    '#d946ef', '#fbbf24', '#64748b', '#dc2626', '#0d9488',
    '#7c3aed', '#2dd4bf', '#f43f5e', '#ea580c', '#be185d',
    '#1d4ed8', '#4338ca', '#047857', '#b45309', '#701a75',
    '#155e75', '#374151', '#6d28d9', '#0369a1', '#a21caf'
  ];

  // Sección de Inventario
  const InventorySection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      {/* KPIs Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total de Productos */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total de Productos</p>
              <p className="text-3xl font-bold text-gray-800">{String(data.totalProducts || 0)}</p>
              <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                <Package className="w-4 h-4" />
                Inventario completo
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Package className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </motion.div>

        {/* Valor Total */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Valor Total</p>
              <p className="text-3xl font-bold text-gray-800">{formatMoney(data.totalValue)}</p>
              <p className="text-sm text-blue-600 flex items-center gap-1 mt-1">
                <DollarSign className="w-4 h-4" />
                Activos del almacén
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </motion.div>

        {/* Productos con Stock Bajo */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Stock Bajo</p>
              <p className="text-3xl font-bold text-gray-800">{String(data.lowStockCount || 0)}</p>
              <p className="text-sm text-orange-600 flex items-center gap-1 mt-1">
                <AlertCircle className="w-4 h-4" />
                Necesitan reabastecimiento
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <AlertCircle className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </motion.div>

        {/* Productos sin Stock */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Sin Stock</p>
              <p className="text-3xl font-bold text-gray-800">{String(data.outOfStockCount || 0)}</p>
              <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                <AlertCircle className="w-4 h-4" />
                Agotados
              </p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Gráfica de Talleres - Full Width */}
      <motion.div
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-purple-600" />
          Talleres con más salidas (últimos {velocityWindowDays} días)
        </h3>
        {Array.isArray(data.topWorkshopsByExits) && data.topWorkshopsByExits.length > 0 ? (
          <>
            {/* Controls removed: simplified view */}

            <ResponsiveContainer width="100%" height={500}>
                <BarChart
                  data={data.topWorkshopsByExits}
                  margin={{ top: 40, right: 20, left: 20, bottom: 80 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  {/* workshop names on X axis; rotate labels for readability */}
                  <XAxis dataKey="name" type="category" interval={0} angle={-45} textAnchor="end" height={70} />
                  <YAxis />
                  <Tooltip formatter={(value) => [value, `Salidas (${velocityWindowDays}d)`]} />
                  <Legend />
                  <Bar dataKey="exits" fill="#6366f1" name={`Salidas (${velocityWindowDays}d)`} barSize={36}>
                    {(data.topWorkshopsByExits || []).map((entry, index) => (
                      <Cell key={`work-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                    <LabelList dataKey="exits" position="top" formatter={(val) => String(val)} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg h-[400px]">
              <Activity className="w-16 h-16 text-gray-400 mb-4" />
              <p className="text-gray-500 text-center font-medium">No hay salidas recientes para mostrar talleres</p>
              <p className="text-gray-400 text-sm text-center mt-2">Ajusta la ventana de tiempo o verifica que existan salidas</p>
            </div>
          )}
      </motion.div>

      {/* Niveles de Stock y otras gráficas en grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Niveles de Stock */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Estado del Inventario
          </h3>
          {Array.isArray(data.stockLevels) && data.stockLevels.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <Treemap
                data={data.stockLevels}
                dataKey="count"
                nameKey="name"
                aspectRatio={4/3}
                stroke="#fff"
                content={(props) => {
                  // Mostrar siempre la tile; sólo ocultar texto si pequeña.
                  if (!props) return null;
                  const { x, y, width, height, index, payload } = props;
                  const color = (payload && payload.color) ? payload.color : COLORS[index % COLORS.length];

                  return (
                    <g>
                      <rect
                        x={x}
                        y={y}
                        width={Math.max(0, width)}
                        height={Math.max(0, height)}
                        style={{
                          fill: color,
                          stroke: '#fff',
                          strokeWidth: 1,
                          strokeOpacity: 0.9,
                        }}
                      />
                      {/* Mostrar etiqueta ajustada para stockLevels */}
                      {payload && payload.name ? (
                        (() => {
                          const area = Math.max(1, width * height);
                          const baseFont = Math.max(8, Math.min(12, Math.floor(Math.sqrt(area) / 4)));
                          const maxChars = Math.max(3, Math.floor(width / 8));
                          let label = String(payload.name || '');
                          if (label.length > maxChars) {
                            label = label.substring(0, Math.max(0, maxChars - 1)).trim() + '…';
                          }

                          return (
                            <text
                              x={x + Math.max(6, width / 2)}
                              y={y + Math.max(baseFont, height / 2)}
                              textAnchor="middle"
                              fill="#fff"
                              fontSize={baseFont}
                              fontWeight="bold"
                              dominantBaseline="middle"
                            >
                              {label}
                            </text>
                          );
                        })()
                      ) : null}
                    </g>
                  );
                }}
              >
                <Tooltip 
                  formatter={(value) => value}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}
                />
              </Treemap>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg h-[300px]">
              <Database className="w-16 h-16 text-gray-400 mb-4" />
              <p className="text-gray-500 text-center font-medium">No hay datos de inventario disponibles</p>
              <p className="text-gray-400 text-sm text-center mt-2">Intenta cargar nuevamente los datos</p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Top Products y Alertas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Productos por Valor */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            Top Productos por Valor
          </h3>
          {/* Lista simple sin scroll limitado (se muestran los top 10 directamente) */}
          <div className="space-y-3">
            {(data.topValueProducts || []).length > 0 ? (data.topValueProducts || []).map((product, index) => {
              if (!product || typeof product !== 'object') return null;
              return (
                <div
                  key={`top-product-${String(product.id || index)}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => { try { navigate(`/inventory/product/${product.id}`); } catch(e){ console.error('Navigate error', e); } }}
                  onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') { navigate(`/inventory/product/${product.id}`); } }}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-semibold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{String(product.name || 'Sin nombre')}</p>
                      <p className="text-sm text-gray-500">{String(product.sku || product.code || 'Sin SKU')}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-800">{formatMoney(product.totalValue || 0)}</p>
                    <p className="text-sm text-gray-500">{String(product.current_stock || product.stock || product.quantity || 0)} unidades</p>
                  </div>
                </div>
              );
            }).filter(Boolean) : (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600">No hay productos para mostrar</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Productos con Stock Bajo */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            Alertas de Stock Bajo
          </h3>
          {/* Contenedor scroll: limitar visualización a ~10 ítems (altura ajustada) */}
          <div className="space-y-3 max-h-[700px] overflow-y-auto pr-1 scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-red-300">
            {(data.lowStockProducts || []).length > 0 ? (
              (data.lowStockProducts || []).map((product, index) => {
                if (!product || typeof product !== 'object') return null;
                const current = Number(product.current_stock || product.stock || product.quantity || 0) || 0;
                const min = Number(product.min_stock || 0) || 0;
                const max = Number(product.max_stock || 50) || 50;
                const isCritical = current <= min;
                const isLow = !isCritical && current < max * 0.7;
                const bgClass = isCritical ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100';
                const qtyClass = isCritical ? 'text-red-600' : 'text-yellow-600';
                const labelClass = isCritical ? 'text-red-500' : 'text-yellow-600';
                const alertLabel = isCritical ? '¡Crítico!' : '¡Reabastecer!';
                return (
                  <div
                    key={`low-stock-${String(product.id || index)}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => { try { if (product.id) { navigate(`/inventory/product/${product.id}`); } else { console.warn('Navegación omitida: product.id ausente', product); } } catch(e){ console.error('Navigate error', e); } }}
                    onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') { if (product.id) { navigate(`/inventory/product/${product.id}`); } else { console.warn('Navegación omitida: product.id ausente', product); } } }}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${bgClass}`}
                  >
                    <div>
                      <p className="font-medium text-gray-800">{String(product.name || 'Sin nombre')}</p>
                      <p className="text-sm text-gray-500">{String(product.sku || product.code || 'Sin SKU')}</p>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${qtyClass}`}>{current} unidades</p>
                      <p className={`text-sm font-medium ${labelClass}`}>{alertLabel}</p>
                    </div>
                  </div>
                );
              }).filter(Boolean)
            ) : (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="text-gray-600">¡Todos los productos tienen stock suficiente!</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

  // Sección de Entradas/Salidas
  const MovementsSection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      {/* KPIs Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Entradas */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Entradas</p>
              <p className="text-3xl font-bold text-gray-800">{String(data.totalEntries || 0)}</p>
              <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                <ArrowUp className="w-4 h-4" />
                Valor: {formatMoney(data.entriesValue)}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <LogIn className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </motion.div>

        {/* Total Salidas */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Salidas</p>
              <p className="text-3xl font-bold text-gray-800">{String(data.totalExits || 0)}</p>
              <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                <ArrowDown className="w-4 h-4" />
                Valor: {formatMoney(data.exitsValue)}
              </p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <LogOut className="w-8 h-8 text-red-600" />
            </div>
          </div>
        </motion.div>

        {/* Balance Neto */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Balance Neto</p>
              <p className="text-3xl font-bold text-gray-800">{formatMoney(data.netMovement)}</p>
              <p className={`text-sm flex items-center gap-1 mt-1 ${data.netMovement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {data.netMovement >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {data.netMovement >= 0 ? 'Positivo' : 'Negativo'}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${data.netMovement >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              <DollarSign className={`w-8 h-8 ${data.netMovement >= 0 ? 'text-green-600' : 'text-red-600'}`} />
            </div>
          </div>
        </motion.div>

        {/* Rotación de Inventario */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Rotación</p>
              <p className="text-3xl font-bold text-gray-800">{(data.turnoverRate || 0).toFixed(1)}%</p>
              <p className="text-sm text-blue-600 flex items-center gap-1 mt-1">
                <Repeat className="w-4 h-4" />
                Eficiencia de movimientos
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Repeat className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Gráficas Principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Movimientos Mensuales */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Movimientos Mensuales
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.monthlyMovements || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip 
                formatter={(value, name) => [
                  name === 'entradas' || name === 'salidas' ? value : formatMoney(value),
                  name === 'entradas' ? 'Entradas' : 
                  name === 'salidas' ? 'Salidas' :
                  name === 'valorEntradas' ? 'Valor Entradas' : 'Valor Salidas'
                ]}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="entradas" 
                stackId="1" 
                stroke="#10b981" 
                fill="#10b981" 
                fillOpacity={0.6}
                name="Entradas"
              />
              <Area 
                type="monotone" 
                dataKey="salidas" 
                stackId="1" 
                stroke="#ef4444" 
                fill="#ef4444" 
                fillOpacity={0.6}
                name="Salidas"
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Valores de Movimientos */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-purple-600" />
            Valores de Movimientos
          </h3>
          <div className="flex items-center justify-end mb-4">
            <button
              onClick={() => setShowDebugData(prev => !prev)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              {showDebugData ? 'Ocultar datos debug' : 'Mostrar datos debug'}
            </button>
          </div>
          {showDebugData && (
            <div className="mb-4 p-3 bg-gray-50 rounded">
              <strong className="text-sm">categoryDistribution:</strong>
              <pre className="text-xs overflow-auto max-h-48 p-2 bg-white rounded mt-1">{JSON.stringify(data.categoryDistribution, null, 2)}</pre>
              <strong className="text-sm mt-2 block">stockLevels:</strong>
              <pre className="text-xs overflow-auto max-h-48 p-2 bg-white rounded mt-1">{JSON.stringify(data.stockLevels, null, 2)}</pre>
            </div>
          )}
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.monthlyMovements || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => formatMoney(value)} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="valorEntradas" 
                stroke="#10b981" 
                name="Valor Entradas"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line 
                type="monotone" 
                dataKey="valorSalidas" 
                stroke="#ef4444" 
                name="Valor Salidas"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Top Productos con Movimientos */}
      <motion.div
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-600" />
          Top Productos con Más Movimientos
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-700">
            <thead className="text-xs text-gray-600 uppercase bg-gray-50">
              <tr>
                <th className="px-4 py-3 rounded-tl-lg">Producto</th>
                <th className="px-4 py-3">SKU</th>
                <th className="px-4 py-3">Entradas</th>
                <th className="px-4 py-3">Salidas</th>
                <th className="px-4 py-3 rounded-tr-lg">Total</th>
              </tr>
            </thead>
            <tbody>
              {(data.topMovementProducts || []).map((product, index) => (
                <tr 
                  key={`movement-${product.id || index}`} 
                  className="bg-white border-b hover:bg-blue-50 cursor-pointer transition-colors"
                  onClick={() => {
                    try {
                      if (product.id) {
                        navigate(`/inventory/product/${product.id}`);
                      } else {
                        console.warn('Producto sin ID:', product);
                      }
                    } catch (e) {
                      console.error('Error navigating to product:', e);
                    }
                  }}
                >
                  <td className="px-4 py-3 font-medium">{product.name}</td>
                  <td className="px-4 py-3">{product.sku || product.code || 'N/A'}</td>
                  <td className="px-4 py-3 text-green-600">{product.movements?.entries || 0}</td>
                  <td className="px-4 py-3 text-red-600">{product.movements?.exits || 0}</td>
                  <td className="px-4 py-3 font-bold">{product.movements?.total || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Métricas de Rendimiento */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100">Promedio de Entrada</p>
              <p className="text-2xl font-bold">{formatMoney(data.averageEntryValue)}</p>
              <p className="text-sm text-blue-100">Por operación</p>
            </div>
            <Calculator className="w-8 h-8 text-blue-200" />
          </div>
        </motion.div>

        <motion.div
          className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl shadow-lg p-6 text-white"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100">Promedio de Salida</p>
              <p className="text-2xl font-bold">{formatMoney(data.averageExitValue)}</p>
              <p className="text-sm text-red-100">Por operación</p>
            </div>
            <Calculator className="w-8 h-8 text-red-200" />
          </div>
        </motion.div>

        <motion.div
          className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.0 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100">Rotación Mensual</p>
              <p className="text-2xl font-bold">{(data.entriesThisMonth + data.exitsThisMonth) || 0}</p>
              <p className="text-sm text-purple-100">Operaciones este mes</p>
            </div>
            <Calendar className="w-8 h-8 text-purple-200" />
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

  // Sección de Proveedores
  const SuppliersSection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      {/* KPIs Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Proveedores */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Proveedores</p>
              <p className="text-3xl font-bold text-gray-800">{String(data.totalSuppliers || 0)}</p>
              <p className="text-sm text-blue-600 flex items-center gap-1 mt-1">
                <Users className="w-4 h-4" />
                Activos en el sistema
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </motion.div>

        {/* Compras Totales */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Compras Totales</p>
              <p className="text-3xl font-bold text-gray-800">{formatMoney(data.totalPurchases)}</p>
              <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                <ShoppingCart className="w-4 h-4" />
                Valor histórico
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <ShoppingCart className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </motion.div>

        {/* Proveedores Principales */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Proveedores Principales</p>
              <p className="text-3xl font-bold text-gray-800">{String(data.topSuppliersCount || 0)}</p>
              <p className="text-sm text-purple-600 flex items-center gap-1 mt-1">
                <Award className="w-4 h-4" />
                80% de las compras
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Award className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </motion.div>

        {/* Último Pedido */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Último Pedido</p>
              <p className="text-3xl font-bold text-gray-800">{data.lastOrderDate || 'N/A'}</p>
              <p className="text-sm text-orange-600 flex items-center gap-1 mt-1">
                <Calendar className="w-4 h-4" />
                Última actividad
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Calendar className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Gráficas Principales */}
      
      {/* Distribución de Compras por Proveedor - Ocupa todo el ancho */}
      <motion.div
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <div className="flex items-center gap-3 mb-4">
          <Users className="w-8 h-8 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-800">Distribución de Compras por Proveedor</h3>
        </div>
        {(() => {
          const rawData = (Array.isArray(data.supplierDistribution) && data.supplierDistribution.length > 0)
            ? data.supplierDistribution
            : [{ name: 'Sin datos', value: 1 }];
          console.log('🧾 RADAR raw data:', rawData);
          
          // Preparar datos para Radar Chart - normalizar valores para mejor visualización
          const maxValue = Math.max(...rawData.map(item => item.value), 1);
          const radarData = rawData.map(item => ({
            subject: item.name,
            Compras: item.value,
            fullMark: maxValue
          }));
          
          console.log('🧾 RADAR formatted data:', radarData);
          
          return (
            <ResponsiveContainer width="100%" height={600}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis 
                  dataKey="subject" 
                  tick={{ fill: '#6b7280', fontSize: 14 }}
                />
                <PolarRadiusAxis 
                  angle={90} 
                  domain={[0, maxValue]}
                  tick={{ fill: '#9ca3af' }}
                />
                <Radar
                  name="Compras"
                  dataKey="Compras"
                  stroke="#3b82f6"
                  fill="#3b82f6"
                  fillOpacity={0.6}
                />
                <Tooltip 
                  formatter={(value) => formatMoney(value)}
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #ccc', 
                    borderRadius: '8px', 
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)' 
                  }}
                />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          );
        })()
        }
      </motion.div>

      {/* Compras Mensuales */}
      <motion.div
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-purple-600" />
          Compras Mensuales
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data.monthlyPurchases || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip formatter={(value) => formatMoney(value)} />
            <Legend />
            <Bar dataKey="value" fill="#8884d8" name="Compras">
              {(data.monthlyPurchases || []).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Top Proveedores */}
      <motion.div
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-yellow-600" />
          Top Proveedores por Volumen de Compra
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-700">
            <thead className="text-xs text-gray-600 uppercase bg-gray-50">
              <tr>
                <th className="px-4 py-3 rounded-tl-lg">Proveedor</th>
                <th className="px-4 py-3">Contacto</th>
                <th className="px-4 py-3">Total Compras</th>
                <th className="px-4 py-3">Últimas Compras</th>
                <th className="px-4 py-3 rounded-tr-lg">Productos</th>
              </tr>
            </thead>
            <tbody>
              {(data.topSuppliers || []).map((supplier, index) => (
                <tr 
                  key={`supplier-${supplier.id || index}`} 
                  className="bg-white border-b hover:bg-blue-50 cursor-pointer transition-colors"
                  onClick={() => {
                    try {
                      navigate(`/suppliers/supplier/${supplier.id}`);
                    } catch (e) {
                      console.error('Error navigating to supplier:', e);
                    }
                  }}
                >
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium">{supplier.name}</p>
                        <p className="text-xs text-gray-500">{supplier.location || 'Sin ubicación'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p>{supplier.contact || 'N/A'}</p>
                    <p className="text-xs text-gray-500">{supplier.phone || supplier.email || 'Sin contacto'}</p>
                  </td>
                  <td className="px-4 py-3 font-bold text-green-600">{formatMoney(supplier.totalPurchases)}</td>
                  <td className="px-4 py-3">{supplier.lastOrderDate || 'N/A'}</td>
                  <td className="px-4 py-3">{supplier.productCount || 0} productos</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </motion.div>
  );

  // Sección de Préstamos
  const LoansSection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      {/* KPIs Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Préstamos */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Préstamos</p>
              <p className="text-3xl font-bold text-gray-800">{String(data.totalLoans || 0)}</p>
              <p className="text-sm text-blue-600 flex items-center gap-1 mt-1">
                <Briefcase className="w-4 h-4" />
                Histórico completo
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Briefcase className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </motion.div>

        {/* Préstamos Activos */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Préstamos Activos</p>
              <p className="text-3xl font-bold text-gray-800">{String(data.activeLoansCount || 0)}</p>
              <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                <CheckCircle className="w-4 h-4" />
                En progreso
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </motion.div>

        {/* Préstamos Vencidos */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Préstamos Vencidos</p>
              <p className="text-3xl font-bold text-gray-800">{String(data.overdueLoanCount || 0)}</p>
              <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                <AlertCircle className="w-4 h-4" />
                Requieren atención
              </p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
        </motion.div>

        {/* Valor en Préstamo */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Valor en Préstamo</p>
              <p className="text-3xl font-bold text-gray-800">{formatMoney(data.loanValue || 0)}</p>
              <p className="text-sm text-purple-600 flex items-center gap-1 mt-1">
                <DollarSign className="w-4 h-4" />
                Activos y vencidos
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <DollarSign className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Gráficas Principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Estado de Préstamos */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Estado de Préstamos
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart 
              data={data.loansByStatus || []} 
              layout="vertical"
              margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={90}
                tick={{ fontSize: 12 }}
              />
              <Tooltip formatter={(value) => value} />
              <Legend />
              <Bar 
                dataKey="value" 
                name="Cantidad"
              >
                {(data.loansByStatus || []).map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={
                      entry.name === 'Activo' ? '#10b981' :
                      entry.name === 'Vencido' ? '#ef4444' :
                      entry.name === 'Completado' ? '#3b82f6' :
                      entry.name === 'Standby' ? '#9333ea' :
                      COLORS[index % COLORS.length]
                    } 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Préstamos por Mes */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-600" />
            Préstamos por Mes
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.loansByMonth || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="prestados" fill="#3b82f6" name="Prestados" />
              <Bar dataKey="devueltos" fill="#10b981" name="Devueltos" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Préstamos Activos */}
      <motion.div
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-blue-600" />
          Préstamos Activos
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-700">
            <thead className="text-xs text-gray-600 uppercase bg-gray-50">
              <tr>
                <th className="px-4 py-3 rounded-tl-lg">ID</th>
                <th className="px-4 py-3">Fecha Préstamo</th>
                <th className="px-4 py-3">Fecha Devolución</th>
                <th className="px-4 py-3">Solicitante</th>
                <th className="px-4 py-3">Productos</th>
                <th className="px-4 py-3">Valor</th>
                <th className="px-4 py-3 rounded-tr-lg">Estado</th>
              </tr>
            </thead>
            <tbody>
              {(data.activeLoans || []).map((loan, index) => (
                <tr 
                  key={`loan-${loan.id || index}`} 
                  className="bg-white border-b hover:bg-gray-50 cursor-pointer" 
                  onClick={() => goToLoanDetail(loan.id)}
                >
                  <td className="px-4 py-3 font-medium">{loan.id}</td>
                  <td className="px-4 py-3">{loan.loanDate}</td>
                  <td className="px-4 py-3">{loan.returnDate}</td>
                  <td className="px-4 py-3">{loan.requester}</td>
                  <td className="px-4 py-3">{loan.productDescription ? `${loan.productCount} (${loan.productDescription})` : `${loan.productCount} item(s)`}</td>
                  <td className="px-4 py-3 font-bold">{formatMoney(loan.value)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      loan.isOverdue ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {loan.isOverdue ? 'Vencido' : 'Activo'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Resumen de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100">Tiempo Promedio</p>
              <p className="text-2xl font-bold">{data.averageLoanDuration || 0} días</p>
              <p className="text-sm text-blue-100">Duración de préstamos</p>
            </div>
            <Clock className="w-8 h-8 text-blue-200" />
          </div>
        </motion.div>

        <motion.div
          className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100">Tasa de Devolución</p>
              <p className="text-2xl font-bold">{data.returnRate || 0}%</p>
              <p className="text-sm text-green-100">Devoluciones a tiempo</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-200" />
          </div>
        </motion.div>

        <motion.div
          className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.0 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100">Solicitante Frecuente</p>
              <p className="text-2xl font-bold">{data.topRequester || 'N/A'}</p>
              <p className="text-sm text-purple-100">{data.topRequesterLoans || 0} préstamos</p>
            </div>
            <User className="w-8 h-8 text-purple-200" />
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

  // Renderizar la sección activa
  return (
    <div className="px-4 py-6">
      <TabsNavigation />
      {/* Date filters placed at top of dashboard */}
      <div className="mb-4 flex items-center justify-between">
        <DateFilters />
        {/* Botones de acceso rápido */}
        <div className="flex gap-3">
          <button
            onClick={() => setShowReportModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Reporte General
          </button>
          <button
            onClick={handleGenerateHistoricalReport}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Reporte Histórico
          </button>
          <button
            onClick={() => navigate('/fleet-tracking')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
          >
            <MapPin className="w-4 h-4" />
            Fleet Tracking
          </button>
        </div>
      </div>
      
      {/* Modal para generar reporte */}
      <AnimatePresence>
        {showReportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowReportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Printer className="w-6 h-6 text-blue-600" />
                  Generar Reporte General
                </h3>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Seleccione una fecha específica o un rango de fechas para generar el reporte completo del almacén 
                con todas las métricas, entradas, salidas y gráficas.
              </p>

              {/* Selector de modo */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setReportMode('single')}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                    reportMode === 'single'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Fecha Única
                </button>
                <button
                  onClick={() => setReportMode('range')}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                    reportMode === 'range'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Rango de Fechas
                </button>
              </div>

              <div className="space-y-4">
                {reportMode === 'single' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha del Reporte
                    </label>
                    <input
                      type="date"
                      value={reportDate}
                      onChange={(e) => setReportDate(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha de Inicio
                      </label>
                      <input
                        type="date"
                        value={reportStartDate}
                        onChange={(e) => setReportStartDate(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha Final
                      </label>
                      <input
                        type="date"
                        value={reportEndDate}
                        onChange={(e) => setReportEndDate(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handleGenerateTodayReport}
                    disabled={generatingReport}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Calendar className="w-5 h-5" />
                    {generatingReport ? 'Generando...' : 'Reporte de Hoy'}
                  </button>
                  
                  <button
                    onClick={handleGenerateReportClick}
                    disabled={generatingReport}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-5 h-5" />
                    {generatingReport ? 'Generando...' : (reportMode === 'range' ? 'Generar Rango' : 'Generar')}
                  </button>
                </div>

                <button
                  onClick={() => setShowReportModal(false)}
                  className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <AnimatePresence mode="wait">
        {activeTab === 'inventory' && <InventorySection key="inventory" />}
        {activeTab === 'movements' && <MovementsSection key="movements" />}
        {activeTab === 'suppliers' && <SuppliersSection key="suppliers" />}
        {activeTab === 'loans' && <LoansSection key="loans" />}
      </AnimatePresence>
    </div>
  );
}

export default SummaryPage;
